/**
 * Brand type for creating nominal types in TypeScript.
 * Allows creating distinct types from the same underlying type to prevent mixing them.
 * Brands can be nested - Brand<Brand<number, "A">, "B"> is assignable to Brand<number, "A">.
 *
 * @example
 * type UserId = Brand<string, 'UserId'>;
 * type ProductId = Brand<string, 'ProductId'>;
 *
 * const userId: UserId = 'user-123' as UserId;
 * const productId: ProductId = 'prod-456' as ProductId;
 * // These cannot be assigned to each other despite both being strings
 *
 * @example
 * type Pointer = Brand<number, "Pointer">
 * type PointerDoc = Brand<Pointer, "PointerDoc">
 * // PointerDoc is assignable to Pointer, but not vice versa
 */
export type Brand<T, TBrand = symbol> = {
  readonly [K in `__${string & TBrand}__`]: true
} & T

/**
 * Expands type aliases to show all fields in IDE tooltips.
 *
 * Forces TypeScript to display the full structure instead of just the type name.
 * Useful for improving developer experience with complex union types.
 *
 * @example
 * ```ts
 * type User = { name: string } & { age: number }
 * type ExpandedUser = Expand<User> // Shows: { name: string; age: number }
 * ```
 */
export type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never

/**
 * Creates mutually exclusive union (one or the other, not both).
 *
 * Ensures only properties from one type can be present at a time.
 * Similar to Maybe<T> pattern for discriminated unions.
 *
 * @example
 * ```ts
 * type BinaryPdf = { source: File; page: undefined }
 * type PlottedPdf = { source: undefined; page: Page }
 * type SinglePagePdf = XOR<BinaryPdf, PlottedPdf>
 * // Can have source OR page, but not both
 * ```
 */
export type XOR<T, U> =
  | ({ [K in Exclude<keyof T, keyof U>]?: never } & U)
  | ({ [K in Exclude<keyof U, keyof T>]?: never } & T)
